/**
 * @bos/portal3 — THE RUN SHELL (route `/app/runs/:id`, inside <AppShell>): the hallway of
 * a build, with the rooms hung off it as real URLs.
 *
 * WHAT CHANGED, AND WHY. This screen used to be the whole life of a run on one scroll:
 * header → vitals → rail → thread (7,201 items on a long build) → documents → download →
 * ledger → whichever single review the rail swapped in at the bottom. The rail was
 * navigation that did not navigate — it re-skinned the bottom of the same page. That is
 * the owner's own complaint about the product: "we put everything in one screen and you
 * handle the rest". So the run became a building:
 *
 *   /app/runs/:id                  → the Overview: the front desk
 *   /app/runs/:id/plan/…           → one room per stage of the plan
 *   /app/runs/:id/build/code       → the code (a read surface is still to come)
 *   /app/runs/:id/build/screens    → the screens (likewise)
 *   /app/runs/:id/verdict          → the verdict
 *   /app/runs/:id/journal          → the full thread, demoted to the forensic record
 *
 * The shell keeps what is true everywhere: who this build is for, what its status is, the
 * stop/resume doors, the nav — and, above every room, THE WAITING BANNER. A halted run
 * used to be invisible from any scroll position but the bottom of the thread; now the
 * machine says what it is waiting for and where the door is, from every room.
 *
 * REFRESH STILL SURVIVES BY CONSTRUCTION: the shell mounts from `runs.get` (row + journal)
 * and attaches the SSE stream at the last sequence it already holds, and each room rebuilds
 * from its own read surface. A deep link into a room is a first-class address.
 *
 * HONEST WAITING, UNCHANGED: elapsed time and the stamp of the last observed frame are the
 * only waiting signals — no percentages, no invented estimates, and no cost (the owner
 * deferred cost display; nothing here draws a number the server did not send).
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { PATHS } from '../routes';
import { PageHeader } from '../components/PageHeader';
import { useRun, useRunIntake, useRunQuestions } from '../runs/hooks';
import { statusBadgeClass } from '../runs/StartRunSection';
import { DEFAULT_TITLE, useRunHead } from '../runs/head';
import type { RunRoomContext } from '../runs/roomContext';
import {
  askingBannerRoom,
  askingBannerSentence,
  countPhrase,
  routeQuestions,
} from '../runs/questionRouting';
import {
  ROOMS,
  ROOM_GROUP_LABEL,
  deriveRail,
  formatClock,
  formatElapsed,
  isReplayNote,
  isResumableStatus,
  isTerminalStatus,
  lastProgressIso,
  ownerCallOf,
  roomBySlug,
  runDocumentTitle,
  stallKindOf,
  statusLabel,
  statusSentence,
  waitingBannerSentence,
  type RailStage,
  type RoomGroup,
} from '../runs/model';

const RAIL_MARK: Record<RailStage['state'], string> = {
  done: '✓',
  current: '▶',
  waiting: '⏸',
  pending: '·',
};

/** The room a path is standing in: null for the Overview (the shell's own index). */
export function roomLabelForPath(pathname: string, runId: string): string | null {
  const base = PATHS.run(runId);
  if (!pathname.startsWith(base)) return null;
  const rest = pathname.slice(base.length).replace(/^\/+/, '').replace(/\/+$/, '');
  if (rest === '') return null;
  if (rest === 'journal') return 'Journal';
  return roomBySlug(rest)?.label ?? null;
}

function NavRow({
  runId,
  slug,
  label,
  state,
  note,
  asking = 0,
}: {
  runId: string;
  slug: string;
  label: string;
  state: RailStage['state'] | null;
  note?: string;
  /** How many of the open round's questions are waiting in this room (0 = none). */
  asking?: number;
}) {
  return (
    <li className={`rnav__row${state !== null ? ` rnav__row--${state}` : ''}${asking > 0 ? ' rnav__row--asking' : ''}`}>
      <NavLink className="rnav__link" to={PATHS.runRoom(runId, slug)} end>
        <span className="rnav__mark" aria-hidden="true">
          {asking > 0 ? '⏸' : state !== null ? RAIL_MARK[state] : '·'}
        </span>
        <span className="rnav__label">
          {label}
          {/* The rail's own waiting mark is about the APPROVAL door; this one is about
              questions routed here. Both can be true, and each says which it is. */}
          {state === 'waiting' && <span className="rnav__waiting"> — waiting for you</span>}
          {asking > 0 && (
            <span className="rnav__waiting"> — {countPhrase(asking)} for you</span>
          )}
        </span>
        {/* The note is clamped to three lines HERE ONLY — a long owner-edit sentence is a
            good thing to have written and a bad thing to have as a nav label. It is on
            screen in full on the Overview's milestones and in the room itself, and the
            title attribute carries it here. */}
        {note !== undefined && (
          <span className="rnav__note" title={note}>
            {note}
          </span>
        )}
      </NavLink>
    </li>
  );
}

export function RunShell() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const { view, status, error, streamState, reload, stop, resume } = useRun(id);

  // THE ROUND, READ ONCE FOR THE WHOLE BUILDING. The shell needs it for the banner and the
  // nav marks; the rooms need their own slice of it. One read, one truth, handed down.
  const ready = status === 'ready';
  const statusKey = view.status ?? '';
  const { questions, qStatus, submit } = useRunQuestions(id, ready, statusKey);
  const intake = useRunIntake(id, ready, statusKey);

  // The honest clock: ticks while the run is live so "running for" / "last progress" stay
  // current. Terminal runs stop ticking — nothing is moving.
  const live = view.status !== null && !isTerminalStatus(view.status);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live]);

  // ── stop / resume (the shell's doors: they belong to the run, not to a room) ──
  const [confirmingStop, setConfirmingStop] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [showRetryResume, setShowRetryResume] = useState(false);

  async function onStop() {
    setActionBusy(true);
    setActionNote(null);
    try {
      const stopped = await stop();
      // {stopped:false} is a NO-OP, not an error: there was nothing to stop.
      if (!stopped) setActionNote('This run is already stopped — there was nothing to do.');
    } catch (err) {
      setActionNote(err instanceof Error ? err.message : 'Could not reach the server.');
    } finally {
      setActionBusy(false);
      setConfirmingStop(false);
    }
  }

  async function onResume() {
    setActionBusy(true);
    setActionNote(null);
    setShowRetryResume(false);
    try {
      const outcome = await resume();
      if (!outcome.ok) {
        if (outcome.kind === 'winding-down') {
          // Not an error: the stopped machinery is still settling. Offer try-again.
          setActionNote('The run is still winding down after the stop — give it a moment.');
          setShowRetryResume(true);
        } else {
          setActionNote(outcome.message);
        }
      }
    } finally {
      setActionBusy(false);
    }
  }

  const roomLabel = roomLabelForPath(location.pathname, id);
  // Until the row is in hand there is no build to name, so the tab says nothing rather
  // than flashing a placeholder name at whoever is watching for one.
  const buildName = view.run === null ? null : (view.run.appName ?? view.run.projectName);
  useRunHead(
    buildName === null ? DEFAULT_TITLE : runDocumentTitle(buildName, roomLabel, view.status),
    view.status === 'awaiting-owner',
  );

  if (status === 'loading') {
    return (
      <section aria-busy="true">
        <p className="page__note">Loading run…</p>
      </section>
    );
  }

  if (status === 'not-found') {
    return (
      <section>
        <PageHeader
          eyebrow="Not found"
          title="Run not found"
          lede="This run doesn’t exist, or it isn’t yours."
        />
        <Link className="btn btn--primary" to={PATHS.dashboard}>
          Back to your projects
        </Link>
      </section>
    );
  }

  if (status === 'error' || view.run === null) {
    return (
      <section>
        <PageHeader eyebrow="Build" title="Something went wrong" />
        <div className="projects__state">
          <p className="form__error" role="alert">
            {error ?? 'We couldn’t load this run.'}
          </p>
          <div className="form__actions">
            <button className="btn btn--ghost" type="button" onClick={() => void reload()}>
              Try again
            </button>
            <Link className="btn btn--quiet" to={PATHS.dashboard}>
              Back to your projects
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const run = view.run;
  const rail = deriveRail(view);
  const railState = (key: string) => rail.find((r) => r.key === key)?.state ?? null;
  const railNote = (key: string) => {
    const note = rail.find((r) => r.key === key)?.note;
    // One line per room, and "replayed free from a checkpoint" is not one: it was the
    // standing subtitle under six rows at once, which is wallpaper, not news.
    return note !== undefined && !isReplayNote(note) ? note : undefined;
  };
  const lastAt = lastProgressIso(view);
  const stallKind = stallKindOf(view);
  const ownerCall = ownerCallOf(view);
  const terminal = view.status !== null && isTerminalStatus(view.status);
  const awaitingOwner = view.status === 'awaiting-owner';

  const roomContext: RunRoomContext = {
    runId: id,
    run,
    view,
    rail,
    ready,
    statusKey,
    terminal,
    ownerCall,
    actionBusy,
    onResume: () => void onResume(),
    questions,
    qStatus,
    submitAnswers: submit,
    intake,
  };

  // WHICH ROOMS HOLD AN OPEN QUESTION — the nav mark and the banner's sentence come from the
  // same fold, so the two can never disagree about where the owner is being asked to go. Only
  // an OPEN round counts: a round the machine has already closed is history, not a door.
  const openRound =
    questions !== null && questions.round.status === 'awaiting-answers' ? questions.round.questions : [];
  const asking = routeQuestions(openRound).byRoom;

  const group = (g: RoomGroup) =>
    ROOMS.filter((r) => r.group === g).map((room) => {
      // One note per rail row: Code and Screens share the build row, and repeating its
      // line under both would say the same thing twice about two different things.
      const first = ROOMS.find((r) => r.railKey === room.railKey);
      const note = first === room ? railNote(room.railKey) : undefined;
      return (
        <NavRow
          key={room.slug}
          runId={id}
          slug={room.slug}
          label={room.label}
          state={railState(room.railKey)}
          asking={asking.get(room.slug)?.length ?? 0}
          {...(note !== undefined ? { note } : {})}
        />
      );
    });

  return (
    <section className="page--run runshell">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to={PATHS.dashboard}>Your projects</Link>
        <span className="crumbs__sep" aria-hidden="true">
          /
        </span>
        <Link to={PATHS.project(run.projectId)}>{run.projectName}</Link>
        <span className="crumbs__sep" aria-hidden="true">
          /
        </span>
        {run.appId !== null && run.appName !== null ? (
          <>
            <Link to={PATHS.app(run.appId)}>{run.appName}</Link>
            <span className="crumbs__sep" aria-hidden="true">
              /
            </span>
          </>
        ) : null}
        {roomLabel === null ? (
          <span className="crumbs__here">Build</span>
        ) : (
          <>
            <Link to={PATHS.run(run.id)}>Build</Link>
            <span className="crumbs__sep" aria-hidden="true">
              /
            </span>
            <span className="crumbs__here">{roomLabel}</span>
          </>
        )}
      </nav>

      {/* THE TITLE IS WHAT WAS BUILT. Since a business can have several apps, the run is
          headed by its APP's name — the same name the finished app is downloaded under.
          A run started before apps existed has none, and is headed by the business it was
          launched for, which is the only name it ever had. */}
      <PageHeader
        eyebrow="Build"
        title={run.appName ?? run.projectName}
        titleAccessory={
          <span className={`badge ${statusBadgeClass(view.status ?? run.status)}`}>
            {statusLabel(view.status)}
          </span>
        }
        lede={statusSentence(view.status, stallKind)}
      />

      <div className="run-vitals" aria-label="Run vitals">
        {run.appName !== null ? (
          <span>
            an app of <Link to={PATHS.project(run.projectId)}>{run.projectName}</Link>
          </span>
        ) : (
          <span>
            built for <Link to={PATHS.project(run.projectId)}>{run.projectName}</Link> before apps
            had names of their own
          </span>
        )}
        <span>started {formatClock(run.startedAt)}</span>
        {view.status === 'running' && (
          <>
            <span>running for {formatElapsed(run.startedAt, now)}</span>
            {lastAt !== null && <span>last progress {formatElapsed(lastAt, now)} ago</span>}
          </>
        )}
        {awaitingOwner && lastAt !== null && (
          <span>
            waiting for {stallKind === 'er-approval' || stallKind === 'stage-approval' ? 'your approval' : 'your answer'} for{' '}
            {formatElapsed(lastAt, now)}
          </span>
        )}
        {terminal && run.endedAt !== null && <span>ended {formatClock(run.endedAt)}</span>}
        {live && streamState === 'reconnecting' && (
          <span className="run-vitals__stream">reconnecting…</span>
        )}
        {live && streamState === 'open' && <span className="run-vitals__stream">live</span>}
      </div>

      <div className="run-actions">
        {live &&
          (confirmingStop ? (
            <>
              <span className="run-actions__confirm">
                Stop this run? Everything already checkpointed is kept — you can resume later.
              </span>
              <button
                className="btn btn--ghost btn--sm btn--danger"
                type="button"
                onClick={() => void onStop()}
                disabled={actionBusy}
              >
                {actionBusy ? 'Stopping…' : 'Stop it'}
              </button>
              <button
                className="btn btn--quiet btn--sm"
                type="button"
                onClick={() => setConfirmingStop(false)}
                disabled={actionBusy}
              >
                Keep going
              </button>
            </>
          ) : (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              onClick={() => setConfirmingStop(true)}
            >
              Stop
            </button>
          ))}
        {isResumableStatus(view.status) && (
          <button
            className="btn btn--primary btn--sm"
            type="button"
            onClick={() => void onResume()}
            disabled={actionBusy}
          >
            {actionBusy ? 'Resuming…' : 'Resume'}
          </button>
        )}
        {actionNote !== null && (
          <span className="run-actions__note" role="status">
            {actionNote}
            {showRetryResume && (
              <button
                className="btn btn--quiet btn--sm"
                type="button"
                onClick={() => void onResume()}
                disabled={actionBusy}
              >
                Try again
              </button>
            )}
          </span>
        )}
      </div>

      {/* THE WAITING BANNER (prior review B3), sticky across every room: a stopped machine
          must never be quieter than a working one. It names the wait and points at the
          door — no scroll position, and no open room, can hide it. */}
      {ownerCall !== null && (
        <div className="rwait" role="status">
          <span className="rwait__mark" aria-hidden="true">
            ⏸
          </span>
          <span className="rwait__text">
            The machine is waiting for you —{' '}
            {/* A round in hand names the ROOMS the questions are in; without one the banner
                keeps the plain sentence rather than guessing at rooms it cannot see. */}
            {ownerCall.kind === 'questions' && openRound.length > 0
              ? askingBannerSentence(openRound)
              : waitingBannerSentence(ownerCall)}
            .
          </span>
          <Link
            className="btn btn--primary btn--sm rwait__go"
            to={
              ownerCall.kind === 'stage-door'
                ? PATHS.runRoom(id, ownerCall.room)
                : ownerCall.kind === 'questions' && askingBannerRoom(openRound) !== null
                  ? PATHS.runRoom(id, askingBannerRoom(openRound) as string)
                  : PATHS.run(id)
            }
          >
            Go there
          </Link>
        </div>
      )}

      <div className="runshell__body">
        <nav className="runshell__nav" aria-label="Rooms of this build">
          <ul className="rnav">
            <NavRow runId={id} slug="" label="Overview" state={null} />
          </ul>
          {(['plan', 'build'] as const).map((g) => (
            <div key={g} className="rnav__group">
              <p className="rnav__grouptitle">{ROOM_GROUP_LABEL[g]}</p>
              <ul className="rnav">{group(g)}</ul>
            </div>
          ))}
          <ul className="rnav rnav--aside">
            <NavRow runId={id} slug="journal" label="Journal" state={null} />
          </ul>
        </nav>

        <div className="runshell__room">
          <Outlet context={roomContext} />
        </div>
      </div>
    </section>
  );
}
